import React, { useState, useEffect, useRef } from 'react';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import {jsPDF} from 'jspdf';
import {autoTable} from 'jspdf-autotable';
import * as XLSX from 'xlsx';

const CRMOpportunities = () => {
  const [summary, setSummary] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [originChartData, setOriginChartData] = useState([]);
  const [lossReasonChartData, setLossReasonChartData] = useState([]);
  const [businessModalityChartData, setBusinessModalityChartData] = useState([]);
  const [salespersonChartData, setSalespersonChartData] = useState([]);
  const [chartDataValue, setChartDataValue] = useState([]);
  const [allOpportunitiesForExport, setAllOpportunitiesForExport] = useState([]);
  const [stageChartMode, setStageChartMode] = useState('quantity');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOpportunities, setTotalOpportunities] = useState(0);
  
  // Estados para exportação e sincronização
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncDates, setSyncDates] = useState({
    startDate: '',
    endDate: ''
  });
  
  // Estado para ordenação
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'asc'
  });
  
  // Filtros expandidos
  const [filters, setFilters] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    estagio: [],
    status: [],
    vendedor: '',
    search: ''
  });

  // Estados para dropdowns multi-select
  const [estagioDropdownOpen, setEstagioDropdownOpen] = useState(false);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const estagioDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);

  const exportMenuRef = useRef(null);

  // Fechar menu de exportação ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    if (showExportMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  // Fechar dropdowns multi-select ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (estagioDropdownRef.current && !estagioDropdownRef.current.contains(event.target)) {
        setEstagioDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
    };
    if (estagioDropdownOpen || statusDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [estagioDropdownOpen, statusDropdownOpen]);

  // Função para obter cor do estágio consistente
  const getEstagioColor = (estagio) => {
    const estagioLower = estagio.toLowerCase();
    switch(estagioLower) {
      case 'prospectando':
        return { bgColor: '#dbeafe', textColor: '#1e40af' };
      case 'em_cotacao':
        return { bgColor: '#fef3c7', textColor: '#92400e' };
      case 'em_negociacao':
        return { bgColor: '#fed7aa', textColor: '#9a3412' };
      case 'fechada_concretizada':
        return { bgColor: '#dcfce7', textColor: '#166534' };
      case 'fechada_perdida':
        return { bgColor: '#fee2e2', textColor: '#991b1b' };
      default:
        return { bgColor: '#f3f4f6', textColor: '#374151' };
    }
  };

  // Função para ordenar dados
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
    loadData(1, { key, direction });
  };

  // Função para ordenar os dados da tabela
  const getSortedOpportunities = () => {
    return opportunities; // Ordenação feita no servidor
  };

  // Componente para cabeçalho ordenável
  const SortableHeader = ({ label, sortKey, style }) => (
    <th 
      onClick={() => handleSort(sortKey)}
      style={{ 
        ...style,
        cursor: 'pointer',
        userSelect: 'none',
        position: 'relative',
        transition: 'background-color 0.2s ease'
      }}
      onMouseOver={(e) => e.target.style.backgroundColor = '#f1f5f9'}
      onMouseOut={(e) => e.target.style.backgroundColor = '#f8fafc'}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {label}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
          <svg 
            width="8" 
            height="4" 
            viewBox="0 0 8 4" 
            style={{ 
              fill: sortConfig.key === sortKey && sortConfig.direction === 'asc' ? '#3b82f6' : '#d1d5db' 
            }}
          >
            <path d="M0 4L4 0L8 4H0Z"/>
          </svg>
          <svg 
            width="8" 
            height="4" 
            viewBox="0 0 8 4" 
            style={{ 
              fill: sortConfig.key === sortKey && sortConfig.direction === 'desc' ? '#3b82f6' : '#d1d5db' 
            }}
          >
            <path d="M0 0L4 4L8 0H0Z"/>
          </svg>
        </div>
      </div>
    </th>
  );

  // Função para buscar TODOS os dados para os gráficos (sem paginação)
  const loadChartData = async () => {
    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        return;
      }
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Buscar TODOS os dados sem paginação para os gráficos
      const chartParams = new URLSearchParams({ page_size: '9999' });
      if (filters.startDate) chartParams.append('startDate', filters.startDate);
      if (filters.endDate) chartParams.append('endDate', filters.endDate);
      if (filters.estagio && filters.estagio.length > 0) chartParams.append('estagio', filters.estagio.join(','));
      if (filters.status && filters.status.length > 0) chartParams.append('status', filters.status.join(','));
      if (filters.vendedor) chartParams.append('vendedor', filters.vendedor);
      if (filters.search) chartParams.append('search', filters.search);

      const chartResponse = await fetch(
        `/dashboard/api/crm/opportunities/?${chartParams}`,
        { headers }
      );

      if (chartResponse.ok) {
        const chartData = await chartResponse.json();
        const allResults = chartData.results || [];
        console.log('📊 Dados completos para gráficos:', allResults.length, 'registros');
        setAllOpportunitiesForExport(allResults);
        processChartData(allResults);
      }
      
    } catch (error) {
      console.error('Erro ao carregar dados do gráfico:', error);
    }
  };

  const loadData = async (page = 1, customSort = null) => {
    try {
      setLoading(true);

      const currentSort = customSort || sortConfig;

      const token = localStorage.getItem('access_token');

      if (!token) {
        console.error('Nenhum token encontrado no localStorage');
        alert('Sessão expirada. Faça login novamente.');
        window.location.href = '/login';
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      // Construir parâmetros de query (tratando arrays para multi-select)
      const buildParams = (baseParams = {}) => {
        const params = new URLSearchParams(baseParams);
        if (filters.startDate) params.append('startDate', filters.startDate);
        if (filters.endDate) params.append('endDate', filters.endDate);
        if (filters.estagio && filters.estagio.length > 0) params.append('estagio', filters.estagio.join(','));
        if (filters.status && filters.status.length > 0) params.append('status', filters.status.join(','));
        if (filters.vendedor) params.append('vendedor', filters.vendedor);
        if (filters.search) params.append('search', filters.search);
        return params;
      };

      const summaryParams = buildParams();
      const listParams = buildParams({ page: page.toString(), page_size: '20' });
      if (currentSort.key) {
        listParams.append('sortBy', currentSort.key);
        listParams.append('sortDirection', currentSort.direction);
      }

      // Buscar dados para summary e tabela
      const [summaryResponse, opportunitiesResponse] = await Promise.all([
        fetch(`/dashboard/api/crm/opportunities/summary/?${summaryParams}`, { headers }),
        fetch(`/dashboard/api/crm/opportunities/?${listParams}`, { headers })
      ]);

      if (!summaryResponse.ok) {
        const errText = await summaryResponse.text().catch(() => '');
        console.error('Summary error:', summaryResponse.status, errText);
        throw new Error(`Erro ao buscar resumo: ${summaryResponse.status}`);
      }
      if (!opportunitiesResponse.ok) {
        const errText = await opportunitiesResponse.text().catch(() => '');
        console.error('Opportunities error:', opportunitiesResponse.status, errText);
        throw new Error(`Erro ao buscar oportunidades: ${opportunitiesResponse.status}`);
      }

      const summaryData = await summaryResponse.json();
      const opportunitiesData = await opportunitiesResponse.json();
      
      setSummary(summaryData);
      setOpportunities(opportunitiesData.results || []);
      setTotalPages(opportunitiesData.total_pages || 1);
      setTotalOpportunities(opportunitiesData.count || 0);
      setCurrentPage(page);

      // Buscar dados completos para os gráficos (TODOS os registros, não apenas a página atual)
      loadChartData();
      
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      alert('Erro ao carregar dados: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const processChartData = (opportunitiesData) => {
    if (!opportunitiesData || opportunitiesData.length === 0) {
      setChartData([]);
      setOriginChartData([]);
      setLossReasonChartData([]);
      setBusinessModalityChartData([]);
      setSalespersonChartData([]);
      return;
    }
    
    buildChartFromOpportunities(opportunitiesData);
    buildOriginChartFromOpportunities(opportunitiesData);
    buildLossReasonChart(opportunitiesData);
    buildBusinessModalityChart(opportunitiesData);
    buildSalespersonChart(opportunitiesData);
  };

  const buildChartFromOpportunities = (opportunitiesData) => {
    const monthlyData = {};
    const monthlyValueData = {};
    const monthNames = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

    opportunitiesData.forEach((opp) => {
      const date = new Date(opp.data_cadastro);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = `${monthNames[date.getMonth()]}/${date.getFullYear()}`;

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          month: monthName,
          Prospectando: 0,
          'Em Cotação': 0,
          'Em Negociação': 0,
          'Fechada - Concretizada': 0,
          'Fechada - Perdida': 0
        };
        monthlyValueData[monthKey] = {
          month: monthName,
          Prospectando: 0,
          'Em Cotação': 0,
          'Em Negociação': 0,
          'Fechada - Concretizada': 0,
          'Fechada - Perdida': 0
        };
      }

      // Mapear estágio para o gráfico
      const estagioLower = opp.estagio.toLowerCase();
      let chartEstagio = 'Prospectando'; // default

      switch(estagioLower) {
        case 'prospectando':
          chartEstagio = 'Prospectando';
          break;
        case 'em_cotacao':
          chartEstagio = 'Em Cotação';
          break;
        case 'em_negociacao':
          chartEstagio = 'Em Negociação';
          break;
        case 'fechada_concretizada':
          chartEstagio = 'Fechada - Concretizada';
          break;
        case 'fechada_perdida':
          chartEstagio = 'Fechada - Perdida';
          break;
        default:
          chartEstagio = 'Prospectando';
      }

      monthlyData[monthKey][chartEstagio]++;
      monthlyValueData[monthKey][chartEstagio] += parseFloat(opp.valor_estimado || 0);
    });

    const sortedKeys = Object.keys(monthlyData).sort();

    setChartData(sortedKeys.map(key => monthlyData[key]));
    setChartDataValue(sortedKeys.map(key => monthlyValueData[key]));
  };

  const buildOriginChartFromOpportunities = (opportunitiesData) => {
    // Primeiro, coletar todas as origens únicas
    const originsSet = new Set();
    opportunitiesData.forEach((opp) => {
      const origem = (opp.origem || 'Não informada').replace(/^\d+\s*-\s*/, '');
      originsSet.add(origem);
    });

    const origins = Array.from(originsSet).sort();
    const monthNames = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

    // Criar estrutura de dados por mês
    const monthlyOriginData = {};

    opportunitiesData.forEach((opp) => {
      const date = new Date(opp.data_cadastro);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const monthName = `${monthNames[date.getMonth()]}/${date.getFullYear()}`;
      
      if (!monthlyOriginData[monthKey]) {
        monthlyOriginData[monthKey] = {
          month: monthName
        };
        // Inicializar todas as origens com 0
        origins.forEach(origin => {
          monthlyOriginData[monthKey][origin] = 0;
        });
      }
      
      const origem = (opp.origem || 'Não informada').replace(/^\d+\s*-\s*/, '');
      monthlyOriginData[monthKey][origem]++;
    });

    // Converter para array e ordenar por mês
    const originChartArray = Object.keys(monthlyOriginData)
      .sort()
      .map(key => monthlyOriginData[key]);
    
    setOriginChartData(originChartArray);
  };

  const buildLossReasonChart = (opportunitiesData) => {
    // Contar oportunidades por motivo de perda
    const lossReasonCount = {};
    let total = 0;
    
    opportunitiesData.forEach((opp) => {
      // Só contar oportunidades perdidas (fechada_perdida)
      if (opp.estagio === 'fechada_perdida' || opp.status === 'perdida') {
        const motivo = opp.motivo_perda || 'Não informado';
        lossReasonCount[motivo] = (lossReasonCount[motivo] || 0) + 1;
        total++;
      }
    });
    
    // Converter para array com porcentagens
    const chartData = Object.keys(lossReasonCount).map(motivo => ({
      name: motivo,
      value: lossReasonCount[motivo],
      percentage: total > 0 ? ((lossReasonCount[motivo] / total) * 100).toFixed(1) : 0
    })).sort((a, b) => b.value - a.value);
    
    setLossReasonChartData(chartData);
  };

  const buildBusinessModalityChart = (opportunitiesData) => {
    // Contar oportunidades por modalidade de negócio
    const modalityCount = {};
    let total = opportunitiesData.length;
    
    opportunitiesData.forEach((opp) => {
      const modalidade = opp.modalidade_negocio || 'Não informada';
      modalityCount[modalidade] = (modalityCount[modalidade] || 0) + 1;
    });
    
    // Converter para array com porcentagens
    const chartData = Object.keys(modalityCount).map(modalidade => ({
      name: modalidade,
      value: modalityCount[modalidade],
      percentage: total > 0 ? ((modalityCount[modalidade] / total) * 100).toFixed(1) : 0
    })).sort((a, b) => b.value - a.value);
    
    setBusinessModalityChartData(chartData);
  };

  const buildSalespersonChart = (opportunitiesData) => {
    // Contar oportunidades e somar valores por vendedor
    const salespersonStats = {};
    
    opportunitiesData.forEach((opp) => {
      const vendedor = opp.vendedor_nome || 'Não informado';
      
      if (!salespersonStats[vendedor]) {
        salespersonStats[vendedor] = {
          name: vendedor,
          quantidade: 0,
          valor: 0
        };
      }
      
      salespersonStats[vendedor].quantidade++;
      salespersonStats[vendedor].valor += parseFloat(opp.valor_estimado || 0);
    });
    
    // Converter para array e ordenar por quantidade (decrescente)
    const chartData = Object.values(salespersonStats)
      .sort((a, b) => b.quantidade - a.quantidade)
      .slice(0, 10) // Top 10 vendedores
      .map(item => ({
        name: item.name.length > 25 ? item.name.substring(0, 25) + '...' : item.name, // Truncar nomes longos
        fullName: item.name, // Nome completo para o tooltip
        quantidade: item.quantidade,
        valor: parseFloat(item.valor.toFixed(2))
      }));
    
    setSalespersonChartData(chartData);
  };

  const handleFilterChange = (newFilters) => {
    setFilters({ ...filters, ...newFilters });
    setCurrentPage(1);
  };

  const applyFilters = () => {
    loadData(1);
  };

  const validateSyncDates = () => {
    if (!syncDates.startDate || !syncDates.endDate) {
      alert('Data inicial e final são obrigatórias para sincronização');
      return false;
    }

    const startDate = new Date(syncDates.startDate);
    const endDate = new Date(syncDates.endDate);

    if (startDate > endDate) {
      alert('Data inicial deve ser anterior à data final');
      return false;
    }

    const diffDays = (endDate - startDate) / (1000 * 60 * 60 * 24);
    if (diffDays > 365) {
      alert('Período máximo permitido: 365 dias');
      return false;
    }

    return true;
  };

  const handleSync = async () => {
    if (!validateSyncDates()) {
      return;
    }

    try {
      setSyncing(true);
      setShowSyncModal(false);
      
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        alert('Sessão expirada. Faça login novamente.');
        window.location.href = '/login';
        return;
      }
      
      const response = await fetch('/dashboard/api/crm/opportunities/sync/', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          start_date: syncDates.startDate,
          end_date: syncDates.endDate
        })
      });

      const result = await response.json();
      
      if (result.success) {
        alert(
          `Sincronização de oportunidades concluída com sucesso!\n\n` +
          `Estatísticas:\n` +
          `• ${result.total || 0} oportunidades processadas\n` +
          `• ${result.created || 0} oportunidades criadas\n` +
          `• ${result.updated || 0} oportunidades atualizadas\n\n` +
          `Período: ${syncDates.startDate} a ${syncDates.endDate}`
        );
        
        const newFilters = {
          startDate: syncDates.startDate,
          endDate: syncDates.endDate,
          estagio: '',
          status: '',
          search: ''
        };
        
        setFilters(newFilters);
        setCurrentPage(1);
        loadData(1);
      } else {
        alert('Erro na sincronização: ' + (result.message || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('Erro na sincronização:', error);
      alert('Erro na sincronização: ' + error.message);
    } finally {
      setSyncing(false);
    }
  };

  const handlePageChange = (page) => {
    loadData(page);
  };

  const openSyncModal = () => {
    const endDate = new Date().toISOString().split('T')[0];
    const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    
    setSyncDates({
      startDate,
      endDate
    });
    setShowSyncModal(true);
  };

  const formatCurrencyFull = (value) => {
    return `R$ ${parseFloat(value || 0).toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  const formatCurrencyShort = (value) => {
    if (value >= 1000000) {
      return `R$ ${(value / 1000000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`;
    } else if (value >= 1000) {
      return `R$ ${(value / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}k`;
    }
    return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const estagioDisplayName = (estagio) => {
    const map = {
      'prospectando': 'Prospectando',
      'em_cotacao': 'Em Cotação',
      'em_negociacao': 'Em Negociação',
      'fechada_concretizada': 'Fechada - Concretizada',
      'fechada_perdida': 'Fechada - Perdida'
    };
    return map[estagio] || estagio;
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF('landscape');

      doc.setFontSize(18);
      doc.text('Relatório de Oportunidades CRM', 14, 20);

      doc.setFontSize(10);
      doc.text(`Período: ${filters.startDate} a ${filters.endDate}`, 14, 28);
      doc.text(`Gerado em: ${new Date().toLocaleString('pt-BR')}`, 14, 34);

      // Resumo por estágio
      doc.setFontSize(14);
      doc.text('Resumo por Estágio', 14, 45);

      const estagios = ['prospectando', 'em_cotacao', 'em_negociacao', 'fechada_concretizada', 'fechada_perdida'];
      const summaryRows = estagios.map(est => ({
        label: estagioDisplayName(est),
        count: summary?.[est] || 0,
        value: summary?.[est + '_value'] || 0
      })).sort((a, b) => b.value - a.value);
      const summaryBody = summaryRows.map(row => [
        row.label,
        row.count,
        formatCurrencyFull(row.value)
      ]);
      summaryBody.push(['TOTAL', summary?.total || 0, formatCurrencyFull(summary?.total_value || 0)]);

      autoTable(doc, {
        startY: 50,
        head: [['Estágio', 'Quantidade', 'Valor Estimado']],
        body: summaryBody,
        theme: 'grid',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [59, 130, 246] }
      });

      // Listagem
      doc.setFontSize(14);
      const finalY = doc.lastAutoTable.finalY || 50;
      doc.text('Listagem de Oportunidades', 14, finalY + 15);

      const estagioOrder = { prospectando: 1, em_cotacao: 2, em_negociacao: 3, fechada_concretizada: 4, fechada_perdida: 5 };
      const sortedOpps = [...allOpportunitiesForExport].sort((a, b) => {
        const estA = estagioOrder[a.estagio] || 99;
        const estB = estagioOrder[b.estagio] || 99;
        if (estA !== estB) return estA - estB;
        return parseFloat(b.valor_estimado || 0) - parseFloat(a.valor_estimado || 0);
      });

      const tableData = sortedOpps.map(opp => [
        opp.cliente_nome || '-',
        (opp.titulo || '-').substring(0, 40),
        estagioDisplayName(opp.estagio),
        formatCurrencyFull(opp.valor_estimado),
        `${opp.probabilidade || 0}%`,
        opp.vendedor_nome || '-',
        opp.data_cadastro ? new Date(opp.data_cadastro).toLocaleDateString('pt-BR') : '-',
        opp.origem || '-'
      ]);

      autoTable(doc, {
        startY: finalY + 20,
        head: [['Cliente', 'Título', 'Estágio', 'Valor', 'Prob.', 'Vendedor', 'Data', 'Origem']],
        body: tableData,
        theme: 'striped',
        styles: { fontSize: 7 },
        headStyles: { fillColor: [59, 130, 246] },
        margin: { left: 14, right: 14 }
      });

      const fileName = `Oportunidades_CRM_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);
      setShowExportMenu(false);
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      alert('Erro ao gerar PDF: ' + error.message);
    }
  };

  const exportToXLSX = () => {
    try {
      const sortedOpps = [...allOpportunitiesForExport].sort((a, b) => {
        return new Date(b.data_cadastro) - new Date(a.data_cadastro);
      });

      const exportData = sortedOpps.map(opp => ({
        'Cliente': opp.cliente_nome || '-',
        'Título': opp.titulo || '-',
        'Estágio': opp.estagio_display || opp.estagio || '-',
        'Status': opp.status_display || opp.status || '-',
        'Data Cadastro': opp.data_cadastro ? new Date(opp.data_cadastro).toLocaleDateString('pt-BR') : '-',
        'Valor Estimado': `R$ ${parseFloat(opp.valor_estimado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        'Probabilidade': `${opp.probabilidade || 0}%`,
        'Vendedor': opp.vendedor_nome || '-',
        'Origem': opp.origem || '-'
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      ws['!cols'] = [
        { wch: 30 }, { wch: 35 }, { wch: 20 }, { wch: 15 },
        { wch: 14 }, { wch: 18 }, { wch: 14 }, { wch: 25 }, { wch: 20 }
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Oportunidades');

      // Aba de resumo
      const estagioList = ['prospectando', 'em_cotacao', 'em_negociacao', 'fechada_concretizada', 'fechada_perdida'];
      const estagioLabels = {
        prospectando: 'Prospectando', em_cotacao: 'Em Cotação', em_negociacao: 'Em Negociação',
        fechada_concretizada: 'Fechada - Concretizada', fechada_perdida: 'Fechada - Perdida'
      };
      const summaryData = estagioList.map(st => ({
        'Estágio': estagioLabels[st] || st,
        'Quantidade': summary?.[st] || 0,
        'Valor Total': `R$ ${parseFloat(summary?.[st + '_value'] || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      }));
      summaryData.push({
        'Estágio': 'TOTAL',
        'Quantidade': summary?.total || 0,
        'Valor Total': `R$ ${parseFloat(summary?.total_value || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      });
      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      wsSummary['!cols'] = [{ wch: 25 }, { wch: 12 }, { wch: 18 }];
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Resumo');

      const fileName = `Oportunidades_CRM_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);

      setShowExportMenu(false);
    } catch (error) {
      console.error('Erro ao exportar XLSX:', error);
      alert('Erro ao exportar arquivo XLSX: ' + error.message);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading && opportunities.length === 0) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '400px',
        gap: '1rem'
      }}>
        <div style={{ 
          width: '40px', 
          height: '40px', 
          border: '4px solid #e5e7eb', 
          borderTop: '4px solid #3b82f6',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#6b7280', margin: 0 }}>Carregando oportunidades CRM...</p>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div>
      {/* Header da Página */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <div>
          <h1 style={{ color: '#1f2937', margin: 0, fontSize: '2rem', fontWeight: '700' }}>
            Oportunidades CRM
          </h1>
          <p style={{ color: '#6b7280', margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
            Gestão completa com sincronização API M8 - Baseado em etapaNegociacao
          </p>
        </div>
        
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <div ref={exportMenuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={allOpportunitiesForExport.length === 0}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: allOpportunitiesForExport.length === 0 ? '#9ca3af' : '#10b981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: allOpportunitiesForExport.length === 0 ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 2px 4px rgba(16, 185, 129, 0.3)'
              }}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Exportar
            </button>
            {showExportMenu && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: '0.5rem',
                backgroundColor: 'white', borderRadius: '8px',
                boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
                border: '1px solid #e5e7eb', zIndex: 1000, minWidth: '220px', overflow: 'hidden'
              }}>
                <button onClick={exportToXLSX} style={{
                  width: '100%', padding: '0.75rem 1rem', backgroundColor: 'transparent',
                  border: 'none', borderBottom: '1px solid #f1f5f9', textAlign: 'left',
                  cursor: 'pointer', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.75rem',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <svg width="20" height="20" fill="#10b981" viewBox="0 0 24 24">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                  <div>
                    <div style={{ fontWeight: '600', color: '#1f2937' }}>Exportar XLSX</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Planilha Excel com todos os dados</div>
                  </div>
                </button>
                <button onClick={exportToPDF} style={{
                  width: '100%', padding: '0.75rem 1rem', backgroundColor: 'transparent',
                  border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: '0.875rem',
                  display: 'flex', alignItems: 'center', gap: '0.75rem', transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <svg width="20" height="20" fill="#dc2626" viewBox="0 0 24 24">
                    <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                  </svg>
                  <div>
                    <div style={{ fontWeight: '600', color: '#1f2937' }}>Exportar PDF</div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Relatório PDF com resumo e listagem</div>
                  </div>
                </button>
              </div>
            )}
          </div>
          <button
            onClick={openSyncModal}
            disabled={syncing}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: syncing ? '#9ca3af' : '#16a34a',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: syncing ? 'not-allowed' : 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              boxShadow: '0 2px 4px rgba(22, 163, 74, 0.3)'
            }}
          >
            {syncing ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #ffffff40',
                  borderTop: '2px solid white',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Sincronizando...
              </>
            ) : (
              <>
                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Sincronizar Oportunidades
              </>
            )}
          </button>
        </div>
      </div>

      {/* Aviso sobre sincronização se não houver dados */}
      {!loading && opportunities.length === 0 && (
        <div style={{
          backgroundColor: '#f0f9ff',
          border: '1px solid #0ea5e9',
          borderRadius: '12px',
          padding: '1.5rem',
          marginBottom: '2rem',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <svg width="24" height="24" fill="currentColor" style={{ color: '#0ea5e9', minWidth: '24px' }} viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div>
            <h3 style={{ margin: '0 0 0.5rem 0', fontWeight: '600', color: '#0c4a6e', fontSize: '1.125rem' }}>
              Nenhuma oportunidade CRM encontrada
            </h3>
            <p style={{ margin: 0, fontSize: '0.875rem', color: '#0c4a6e', lineHeight: '1.5' }}>
              Configure as datas no botão "Sincronizar Oportunidades" para importar os dados da API M8. 
              O sistema processará automaticamente todas as páginas disponíveis.
            </p>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
        border: '1px solid #f1f5f9',
        marginBottom: '2rem'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
          gap: '1rem'
        }}>
          <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.25rem', fontWeight: '700' }}>
            Filtros de Visualização
          </h3>
        </div>

        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '1.5rem',
          marginBottom: '2rem',
          alignItems: 'flex-end'
        }}>
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Data Inicial
            </label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange({ startDate: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#16a34a'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Data Final
            </label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange({ endDate: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#16a34a'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
          {/* Multi-select Estágio */}
          <div style={{ flex: '1 1 200px', minWidth: '200px', position: 'relative' }} ref={estagioDropdownRef}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Estágio
            </label>
            <button
              type="button"
              onClick={() => { setEstagioDropdownOpen(!estagioDropdownOpen); setStatusDropdownOpen(false); }}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `1px solid ${estagioDropdownOpen ? '#16a34a' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: '0.875rem',
                backgroundColor: 'white',
                cursor: 'pointer',
                outline: 'none',
                boxSizing: 'border-box',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                textAlign: 'left',
                color: filters.estagio.length === 0 ? '#6b7280' : '#1f2937'
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {filters.estagio.length === 0 ? 'Todos os Estágios' : `${filters.estagio.length} selecionado(s)`}
              </span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ minWidth: '12px', transform: estagioDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                <path d="M2 4L6 8L10 4" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {estagioDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '4px',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 50,
                maxHeight: '220px',
                overflowY: 'auto'
              }}>
                {[
                  { value: 'prospectando', label: 'Prospectando' },
                  { value: 'em_cotacao', label: 'Em Cotação' },
                  { value: 'em_negociacao', label: 'Em Negociação' },
                  { value: 'fechada_concretizada', label: 'Fechada - Concretizada' },
                  { value: 'fechada_perdida', label: 'Fechada - Perdida' }
                ].map(opt => (
                  <label
                    key={opt.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      color: '#374151',
                      borderBottom: '1px solid #f3f4f6',
                      backgroundColor: filters.estagio.includes(opt.value) ? '#f0fdf4' : 'white',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseOver={(e) => { if (!filters.estagio.includes(opt.value)) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = filters.estagio.includes(opt.value) ? '#f0fdf4' : 'white'; }}
                  >
                    <input
                      type="checkbox"
                      checked={filters.estagio.includes(opt.value)}
                      onChange={() => {
                        const newEstagio = filters.estagio.includes(opt.value)
                          ? filters.estagio.filter(v => v !== opt.value)
                          : [...filters.estagio, opt.value];
                        handleFilterChange({ estagio: newEstagio });
                      }}
                      style={{ accentColor: '#16a34a', width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Multi-select Status */}
          <div style={{ flex: '1 1 200px', minWidth: '200px', position: 'relative' }} ref={statusDropdownRef}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Status
            </label>
            <button
              type="button"
              onClick={() => { setStatusDropdownOpen(!statusDropdownOpen); setEstagioDropdownOpen(false); }}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `1px solid ${statusDropdownOpen ? '#16a34a' : '#d1d5db'}`,
                borderRadius: '6px',
                fontSize: '0.875rem',
                backgroundColor: 'white',
                cursor: 'pointer',
                outline: 'none',
                boxSizing: 'border-box',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                textAlign: 'left',
                color: filters.status.length === 0 ? '#6b7280' : '#1f2937'
              }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {filters.status.length === 0 ? 'Todos os Status' : `${filters.status.length} selecionado(s)`}
              </span>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ minWidth: '12px', transform: statusDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
                <path d="M2 4L6 8L10 4" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {statusDropdownOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                marginTop: '4px',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                zIndex: 50,
                maxHeight: '220px',
                overflowY: 'auto'
              }}>
                {[
                  { value: 'aberta', label: 'Aberta' },
                  { value: 'fechada', label: 'Fechada' },
                  { value: 'cancelada', label: 'Cancelada' }
                ].map(opt => (
                  <label
                    key={opt.value}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      padding: '0.5rem 0.75rem',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      color: '#374151',
                      borderBottom: '1px solid #f3f4f6',
                      backgroundColor: filters.status.includes(opt.value) ? '#f0fdf4' : 'white',
                      transition: 'background-color 0.15s'
                    }}
                    onMouseOver={(e) => { if (!filters.status.includes(opt.value)) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                    onMouseOut={(e) => { e.currentTarget.style.backgroundColor = filters.status.includes(opt.value) ? '#f0fdf4' : 'white'; }}
                  >
                    <input
                      type="checkbox"
                      checked={filters.status.includes(opt.value)}
                      onChange={() => {
                        const newStatus = filters.status.includes(opt.value)
                          ? filters.status.filter(v => v !== opt.value)
                          : [...filters.status, opt.value];
                        handleFilterChange({ status: newStatus });
                      }}
                      style={{ accentColor: '#16a34a', width: '16px', height: '16px', cursor: 'pointer' }}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Filtro por Vendedor */}
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Vendedor
            </label>
            <input
              type="text"
              placeholder="Nome do vendedor..."
              value={filters.vendedor}
              onChange={(e) => handleFilterChange({ vendedor: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#16a34a'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>

          {/* Busca geral */}
          <div style={{ flex: '1 1 200px', minWidth: '200px' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>
              Busca
            </label>
            <input
              type="text"
              placeholder="Título, cliente..."
              value={filters.search}
              onChange={(e) => handleFilterChange({ search: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.875rem',
                outline: 'none',
                boxSizing: 'border-box'
              }}
              onFocus={(e) => e.target.style.borderColor = '#16a34a'}
              onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
            />
          </div>
        </div>

        <div style={{
          display: 'flex',
          gap: '1rem',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          borderTop: '1px solid #f1f5f9',
          paddingTop: '1.5rem'
        }}>
          <button
            onClick={() => {
              setFilters({
                startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                endDate: new Date().toISOString().split('T')[0],
                estagio: [],
                status: [],
                vendedor: '',
                search: ''
              });
              setEstagioDropdownOpen(false);
              setStatusDropdownOpen(false);
              setCurrentPage(1);
              applyFilters();
            }}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: 'white',
              color: '#4b5563',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#f9fafb';
              e.currentTarget.style.borderColor = '#9ca3af';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'white';
              e.currentTarget.style.borderColor = '#d1d5db';
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Limpar
          </button>

          <button
            onClick={applyFilters}
            style={{
              padding: '0.75rem 1.5rem',
              backgroundColor: '#16a34a',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: '600',
              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#15803d';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#16a34a';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            Aplicar Filtros
          </button>
        </div>
      </div>

      {/* Cards de Resumo - ESTÁGIOS CORRETOS */}
      {summary && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          {[
            { 
              label: 'Prospectando', 
              key: 'prospectando',
              count: summary.prospectando || 0, 
              value: summary.prospectando_value || 0,
              color: '#3b82f6', 
              bgColor: '#dbeafe', 
              textColor: '#1e40af' 
            },
            { 
              label: 'Em Cotação', 
              key: 'em_cotacao',
              count: summary.em_cotacao || 0, 
              value: summary.em_cotacao_value || 0,
              color: '#f59e0b', 
              bgColor: '#fef3c7', 
              textColor: '#92400e' 
            },
            { 
              label: 'Em Negociação', 
              key: 'em_negociacao',
              count: summary.em_negociacao || 0, 
              value: summary.em_negociacao_value || 0,
              color: '#f97316', 
              bgColor: '#fed7aa', 
              textColor: '#9a3412' 
            },
            { 
              label: 'Fechada - Concretizada', 
              key: 'fechada_concretizada',
              count: summary.fechada_concretizada || 0, 
              value: summary.fechada_concretizada_value || 0,
              color: '#22c55e', 
              bgColor: '#dcfce7', 
              textColor: '#166534' 
            },
            { 
              label: 'Fechada - Perdida', 
              key: 'fechada_perdida',
              count: summary.fechada_perdida || 0, 
              value: summary.fechada_perdida_value || 0,
              color: '#dc2626', 
              bgColor: '#fee2e2', 
              textColor: '#991b1b' 
            },
            { 
              label: 'Total', 
              key: 'total',
              count: summary.total || 0, 
              value: summary.total_value || 0,
              color: '#6b7280', 
              bgColor: '#f3f4f6', 
              textColor: '#374151' 
            }
          ].map((item, index) => (
            <div key={index} style={{
              backgroundColor: 'white',
              padding: '1.5rem',
              borderRadius: '12px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
              border: '1px solid #f1f5f9',
              transition: 'transform 0.2s ease, box-shadow 0.2s ease',
              cursor: item.key !== 'total' ? 'pointer' : 'default'
            }}
            onClick={() => {
              if (item.key !== 'total') {
                handleFilterChange({ estagio: item.key });
                applyFilters();
              }
            }}
            onMouseOver={(e) => {
              if (item.key !== 'total') {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 15px rgba(0, 0, 0, 0.1)';
              }
            }}
            onMouseOut={(e) => {
              if (item.key !== 'total') {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.07)';
              }
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, color: item.textColor, fontSize: '0.875rem', fontWeight: '600' }}>{item.label}</h3>
                <div style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: item.color,
                  borderRadius: '50%'
                }}></div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                <div>
                  <p style={{ fontSize: '2.25rem', fontWeight: '800', margin: 0, color: '#1f2937', lineHeight: '1' }}>
                    {item.count}
                  </p>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>oportunidades</p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <p style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0, color: item.textColor }}>
                    R$ {parseFloat(item.value || 0).toLocaleString('pt-BR', { 
                      minimumFractionDigits: 2, 
                      maximumFractionDigits: 2 
                    })}
                  </p>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.25rem 0 0 0' }}>valor total</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Gráfico de Oportunidades por Mês - ESTÁGIOS com toggle quantidade/valor */}
      {chartData.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
          border: '1px solid #f1f5f9',
          marginBottom: '2rem'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
            <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>
              {stageChartMode === 'quantity' ? 'Oportunidades CRM por Mês (por Estágio)' : 'Valor das Oportunidades por Mês (por Estágio)'}
            </h3>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setStageChartMode('quantity')}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  borderRadius: '6px',
                  border: stageChartMode === 'quantity' ? 'none' : '1px solid #d1d5db',
                  backgroundColor: stageChartMode === 'quantity' ? '#3b82f6' : 'white',
                  color: stageChartMode === 'quantity' ? 'white' : '#4b5563',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Oportunidades
              </button>
              <button
                onClick={() => setStageChartMode('value')}
                style={{
                  padding: '0.5rem 1rem',
                  fontSize: '0.8rem',
                  fontWeight: '600',
                  borderRadius: '6px',
                  border: stageChartMode === 'value' ? 'none' : '1px solid #d1d5db',
                  backgroundColor: stageChartMode === 'value' ? '#10b981' : 'white',
                  color: stageChartMode === 'value' ? 'white' : '#4b5563',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Valor das Oportunidades
              </button>
            </div>
          </div>
          <div style={{ width: '100%', height: '400px' }}>
            <ResponsiveContainer>
              <BarChart
                data={stageChartMode === 'quantity' ? chartData : chartDataValue}
                margin={{ top: 30, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  tickLine={{ stroke: '#d1d5db' }}
                  domain={[0, 'dataMax + 5']}
                  tickFormatter={stageChartMode === 'value' ? formatCurrencyShort : undefined}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                  labelStyle={{ color: '#374151', fontWeight: '500' }}
                  formatter={stageChartMode === 'value' ? (value) => formatCurrencyFull(value) : undefined}
                />
                <Legend wrapperStyle={{ paddingTop: '20px' }} />
                <Bar dataKey="Prospectando" fill="#3b82f6" name="Prospectando" radius={[2, 2, 0, 0]}>
                  <LabelList dataKey="Prospectando" position="top" formatter={(v) => v === 0 ? '' : (stageChartMode === 'value' ? formatCurrencyShort(v) : v)} style={{ fontSize: '10px', fill: '#374151', fontWeight: '600' }} />
                </Bar>
                <Bar dataKey="Em Cotação" fill="#f59e0b" name="Em Cotação" radius={[2, 2, 0, 0]}>
                  <LabelList dataKey="Em Cotação" position="top" formatter={(v) => v === 0 ? '' : (stageChartMode === 'value' ? formatCurrencyShort(v) : v)} style={{ fontSize: '10px', fill: '#374151', fontWeight: '600' }} />
                </Bar>
                <Bar dataKey="Em Negociação" fill="#f97316" name="Em Negociação" radius={[2, 2, 0, 0]}>
                  <LabelList dataKey="Em Negociação" position="top" formatter={(v) => v === 0 ? '' : (stageChartMode === 'value' ? formatCurrencyShort(v) : v)} style={{ fontSize: '10px', fill: '#374151', fontWeight: '600' }} />
                </Bar>
                <Bar dataKey="Fechada - Concretizada" fill="#22c55e" name="Fechada - Concretizada" radius={[2, 2, 0, 0]}>
                  <LabelList dataKey="Fechada - Concretizada" position="top" formatter={(v) => v === 0 ? '' : (stageChartMode === 'value' ? formatCurrencyShort(v) : v)} style={{ fontSize: '10px', fill: '#374151', fontWeight: '600' }} />
                </Bar>
                <Bar dataKey="Fechada - Perdida" fill="#dc2626" name="Fechada - Perdida" radius={[2, 2, 0, 0]}>
                  <LabelList dataKey="Fechada - Perdida" position="top" formatter={(v) => v === 0 ? '' : (stageChartMode === 'value' ? formatCurrencyShort(v) : v)} style={{ fontSize: '10px', fill: '#374151', fontWeight: '600' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Gráfico de Oportunidades por Mês - Por Origem */}
      {originChartData.length > 0 && (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '1.5rem', 
          borderRadius: '12px', 
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
          border: '1px solid #f1f5f9',
          marginBottom: '2rem'
        }}>
          <h3 style={{ margin: '0 0 1.5rem 0', color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>
            Oportunidades CRM por Mês (por Origem)
          </h3>
          <div style={{ width: '100%', height: '400px' }}>
            <ResponsiveContainer>
              <BarChart
                data={originChartData}
                margin={{
                  top: 30,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <YAxis 
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  tickLine={{ stroke: '#d1d5db' }}
                  domain={[0, 'dataMax + 5']}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                  labelStyle={{ color: '#374151', fontWeight: '500' }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                />
                {/* Barras dinâmicas baseadas nas origens */}
                {originChartData.length > 0 && Object.keys(originChartData[0])
                  .filter(key => key !== 'month')
                  .map((origem, index) => {
                    // Cores variadas para cada origem
                    const colors = [
                      '#3b82f6', // azul
                      '#10b981', // verde
                      '#f59e0b', // amarelo
                      '#ef4444', // vermelho
                      '#8b5cf6', // roxo
                      '#ec4899', // rosa
                      '#14b8a6', // teal
                      '#f97316', // laranja
                      '#06b6d4', // cyan
                      '#84cc16', // lime
                    ];
                    return (
                      <Bar
                        key={origem}
                        dataKey={origem}
                        fill={colors[index % colors.length]}
                        name={origem}
                        radius={[2, 2, 0, 0]}
                      >
                        <LabelList dataKey={origem} position="top" formatter={(v) => v === 0 ? '' : v} style={{ fontSize: '11px', fill: '#374151', fontWeight: '600' }} />
                      </Bar>
                    );
                  })}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
      
      {/* Gráficos de Pizza - Lado a Lado */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))', 
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* Gráfico de Pizza - Motivo de Perda */}
        {lossReasonChartData.length > 0 && (
          <div style={{ 
            backgroundColor: 'white', 
            padding: '1.5rem', 
            borderRadius: '12px', 
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
            border: '1px solid #f1f5f9'
          }}>
            <h3 style={{ margin: '0 0 1.5rem 0', color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>
              Motivos de Perda
            </h3>
            <div style={{ width: '100%', height: '350px' }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={lossReasonChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name}: ${percentage}%`}
                    outerRadius={100}
                    innerRadius={50}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {lossReasonChartData.map((entry, index) => {
                      const colors = ['#ef4444', '#f97316', '#f59e0b', '#84cc16', '#10b981', '#06b6d4', '#3b82f6', '#8b5cf6', '#ec4899', '#f43f5e'];
                      return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                    })}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, 'Quantidade']}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    formatter={(value, entry) => `${value} (${entry.payload.percentage}%)`}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{ 
              textAlign: 'center', 
              marginTop: '1rem', 
              padding: '0.75rem', 
              backgroundColor: '#fef3c7', 
              borderRadius: '8px' 
            }}>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#92400e', fontWeight: '500' }}>
                Total de oportunidades perdidas: {lossReasonChartData.reduce((sum, item) => sum + item.value, 0)}
              </p>
            </div>
          </div>
        )}

        {/* Gráfico de Pizza - Modalidade de Negócio */}
        {businessModalityChartData.length > 0 && (
          <div style={{ 
            backgroundColor: 'white', 
            padding: '1.5rem', 
            borderRadius: '12px', 
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
            border: '1px solid #f1f5f9'
          }}>
            <h3 style={{ margin: '0 0 1.5rem 0', color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>
              Modalidades de Negócio
            </h3>
            <div style={{ width: '100%', height: '350px' }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={businessModalityChartData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percentage }) => `${name}: ${percentage}%`}
                    outerRadius={100}
                    innerRadius={50}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {businessModalityChartData.map((entry, index) => {
                      const colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16'];
                      return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                    })}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name, props) => [`${value} (${props.payload.percentage}%)`, 'Quantidade']}
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    formatter={(value, entry) => `${value} (${entry.payload.percentage}%)`}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{ 
              textAlign: 'center', 
              marginTop: '1rem', 
              padding: '0.75rem', 
              backgroundColor: '#dbeafe', 
              borderRadius: '8px' 
            }}>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#1e40af', fontWeight: '500' }}>
                Total de oportunidades: {businessModalityChartData.reduce((sum, item) => sum + item.value, 0)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Gráfico de Ranking de Vendedores */}
      {salespersonChartData.length > 0 && (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '1.5rem', 
          borderRadius: '12px', 
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
          border: '1px solid #f1f5f9',
          marginBottom: '2rem'
        }}>
          <h3 style={{ margin: '0 0 1.5rem 0', color: '#1f2937', fontSize: '1.125rem', fontWeight: '600' }}>
            🏆 Ranking de Vendedores - Top 10
          </h3>
          <div style={{ width: '100%', height: '500px' }}>
            <ResponsiveContainer>
              <BarChart
                data={salespersonChartData}
                layout="vertical"
                margin={{
                  top: 20,
                  right: 100,
                  left: 20,
                  bottom: 20,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis 
                  type="number"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  tickLine={{ stroke: '#d1d5db' }}
                />
                <YAxis 
                  type="category"
                  dataKey="name"
                  tick={{ fontSize: 12, fill: '#6b7280' }}
                  tickLine={{ stroke: '#d1d5db' }}
                  width={150}
                />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === 'quantidade') {
                      return [value, 'Oportunidades'];
                    } else if (name === 'valor') {
                      return [`R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 'Valor Total'];
                    }
                    return [value, name];
                  }}
                  labelFormatter={(label) => {
                    const item = salespersonChartData.find(d => d.name === label);
                    return item ? item.fullName : label;
                  }}
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                  }}
                  labelStyle={{ color: '#374151', fontWeight: '600', marginBottom: '0.5rem' }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '20px' }}
                  formatter={(value) => {
                    if (value === 'quantidade') return 'Quantidade de Oportunidades';
                    if (value === 'valor') return 'Valor Total (R$)';
                    return value;
                  }}
                />
                <Bar 
                  dataKey="quantidade" 
                  fill="#3b82f6"
                  name="quantidade"
                  radius={[0, 4, 4, 0]}
                >
                  <LabelList 
                    dataKey="quantidade" 
                    position="right" 
                    style={{ fill: '#1f2937', fontSize: '12px', fontWeight: '600' }}
                  />
                </Bar>
                <Bar 
                  dataKey="valor" 
                  fill="#10b981"
                  name="valor"
                  radius={[0, 4, 4, 0]}
                >
                  <LabelList 
                    dataKey="valor" 
                    position="right"
                    formatter={(value) => `R$ ${(value / 1000).toFixed(0)}k`}
                    style={{ fill: '#1f2937', fontSize: '12px', fontWeight: '600' }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginTop: '1.5rem'
          }}>
            <div style={{ 
              textAlign: 'center', 
              padding: '1rem', 
              backgroundColor: '#dbeafe', 
              borderRadius: '8px' 
            }}>
              <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.75rem', color: '#1e40af', fontWeight: '600', textTransform: 'uppercase' }}>
                Total de Oportunidades
              </p>
              <p style={{ margin: 0, fontSize: '1.5rem', color: '#1e40af', fontWeight: '700' }}>
                {salespersonChartData.reduce((sum, item) => sum + item.quantidade, 0)}
              </p>
            </div>
            <div style={{ 
              textAlign: 'center', 
              padding: '1rem', 
              backgroundColor: '#d1fae5', 
              borderRadius: '8px' 
            }}>
              <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.75rem', color: '#065f46', fontWeight: '600', textTransform: 'uppercase' }}>
                Valor Total
              </p>
              <p style={{ margin: 0, fontSize: '1.5rem', color: '#065f46', fontWeight: '700' }}>
                R$ {salespersonChartData.reduce((sum, item) => sum + item.valor, 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div style={{ 
              textAlign: 'center', 
              padding: '1rem', 
              backgroundColor: '#fef3c7', 
              borderRadius: '8px' 
            }}>
              <p style={{ margin: '0 0 0.25rem 0', fontSize: '0.75rem', color: '#92400e', fontWeight: '600', textTransform: 'uppercase' }}>
                Média por Vendedor
              </p>
              <p style={{ margin: 0, fontSize: '1.5rem', color: '#92400e', fontWeight: '700' }}>
                {Math.round(salespersonChartData.reduce((sum, item) => sum + item.quantidade, 0) / salespersonChartData.length)} opor.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Tabela de Oportunidades */}
      <div style={{ 
        backgroundColor: 'white', 
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07)',
        border: '1px solid #f1f5f9',
        overflow: 'hidden'
      }}>
        <div style={{ 
          padding: '1.5rem', 
          borderBottom: '1px solid #f1f5f9', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: '#fafbfc'
        }}>
          <h2 style={{ margin: 0, color: '#1f2937', fontSize: '1.25rem', fontWeight: '700' }}>
            Listagem de Oportunidades
          </h2>
          <span style={{ 
            color: '#6b7280', 
            fontSize: '0.875rem',
            backgroundColor: '#f3f4f6',
            padding: '0.5rem 1rem',
            borderRadius: '20px',
            fontWeight: '500'
          }}>
            Total: {totalOpportunities} oportunidades | Página {currentPage} de {totalPages}
          </span>
        </div>
        
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center' }}>
            <div style={{ 
              width: '32px', 
              height: '32px', 
              border: '3px solid #e5e7eb', 
              borderTop: '3px solid #16a34a',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 1rem'
            }}></div>
            <p style={{ color: '#6b7280', margin: 0 }}>Carregando oportunidades...</p>
          </div>
        ) : opportunities.length > 0 ? (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f8fafc' }}>
                  <tr>
                    <SortableHeader 
                      label="Cliente" 
                      sortKey="cliente_nome"
                      style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        fontSize: '0.875rem', 
                        fontWeight: '600', 
                        color: '#4b5563', 
                        borderBottom: '2px solid #e5e7eb' 
                      }}
                    />
                    <SortableHeader 
                      label="Título" 
                      sortKey="titulo"
                      style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        fontSize: '0.875rem', 
                        fontWeight: '600', 
                        color: '#4b5563', 
                        borderBottom: '2px solid #e5e7eb' 
                      }}
                    />
                    <SortableHeader 
                      label="Estágio" 
                      sortKey="estagio"
                      style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        fontSize: '0.875rem', 
                        fontWeight: '600', 
                        color: '#4b5563', 
                        borderBottom: '2px solid #e5e7eb' 
                      }}
                    />
                    <SortableHeader 
                      label="Status" 
                      sortKey="status"
                      style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        fontSize: '0.875rem', 
                        fontWeight: '600', 
                        color: '#4b5563', 
                        borderBottom: '2px solid #e5e7eb' 
                      }}
                    />
                    <SortableHeader 
                      label="Data Cadastro" 
                      sortKey="data_cadastro"
                      style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        fontSize: '0.875rem', 
                        fontWeight: '600', 
                        color: '#4b5563', 
                        borderBottom: '2px solid #e5e7eb' 
                      }}
                    />
                    <SortableHeader 
                      label="Valor Estimado" 
                      sortKey="valor_estimado"
                      style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        fontSize: '0.875rem', 
                        fontWeight: '600', 
                        color: '#4b5563', 
                        borderBottom: '2px solid #e5e7eb' 
                      }}
                    />
                    <SortableHeader 
                      label="Probabilidade" 
                      sortKey="probabilidade"
                      style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        fontSize: '0.875rem', 
                        fontWeight: '600', 
                        color: '#4b5563', 
                        borderBottom: '2px solid #e5e7eb' 
                      }}
                    />
                    <SortableHeader 
                      label="Vendedor" 
                      sortKey="vendedor_nome"
                      style={{ 
                        padding: '1rem', 
                        textAlign: 'left', 
                        fontSize: '0.875rem', 
                        fontWeight: '600', 
                        color: '#4b5563', 
                        borderBottom: '2px solid #e5e7eb' 
                      }}
                    />
                  </tr>
                </thead>
                <tbody>
                  {getSortedOpportunities().map((opp, index) => {
                    const estagioColors = getEstagioColor(opp.estagio);
                    return (
                      <tr key={opp.id} style={{ 
                        borderBottom: '1px solid #f1f5f9',
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
                          {opp.cliente_nome || '-'}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {opp.titulo || '-'}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                          <span style={{
                            padding: '0.375rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            backgroundColor: estagioColors.bgColor,
                            color: estagioColors.textColor
                          }}>
                            {opp.estagio_display || opp.estagio}
                          </span>
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151' }}>
                          {opp.status_display || opp.status}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151' }}>
                          {opp.data_cadastro ? new Date(opp.data_cadastro).toLocaleDateString('pt-BR') : '-'}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151', fontWeight: '500' }}>
                          R$ {parseFloat(opp.valor_estimado || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151', textAlign: 'center' }}>
                          {opp.probabilidade}%
                        </td>
                        <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#374151' }}>
                          {opp.vendedor_nome || '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Paginação */}
            {totalPages > 1 && (
              <div style={{ 
                padding: '1.5rem', 
                borderTop: '1px solid #f1f5f9', 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                gap: '0.75rem',
                backgroundColor: '#fafbfc'
              }}>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  style={{
                    padding: '0.75rem 1rem',
                    border: '2px solid #e5e7eb',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    opacity: currentPage === 1 ? 0.5 : 1,
                    fontWeight: '500',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    if (currentPage !== 1) {
                      e.target.style.borderColor = '#16a34a';
                      e.target.style.backgroundColor = '#f8fafc';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.backgroundColor = 'white';
                  }}
                >
                  ← Anterior
                </button>

                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={i}
                      onClick={() => handlePageChange(pageNum)}
                      style={{
                        padding: '0.75rem 1rem',
                        border: `2px solid ${pageNum === currentPage ? '#16a34a' : '#e5e7eb'}`,
                        backgroundColor: pageNum === currentPage ? '#16a34a' : 'white',
                        color: pageNum === currentPage ? 'white' : '#374151',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        fontSize: '0.875rem',
                        minWidth: '44px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => {
                        if (pageNum !== currentPage) {
                          e.target.style.borderColor = '#16a34a';
                          e.target.style.backgroundColor = '#f8fafc';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (pageNum !== currentPage) {
                          e.target.style.borderColor = '#e5e7eb';
                          e.target.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  style={{
                    padding: '0.75rem 1rem',
                    border: '2px solid #e5e7eb',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    opacity: currentPage === totalPages ? 0.5 : 1,
                    fontWeight: '500',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    if (currentPage !== totalPages) {
                      e.target.style.borderColor = '#16a34a';
                      e.target.style.backgroundColor = '#f8fafc';
                    }
                  }}
                  onMouseOut={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.backgroundColor = 'white';
                  }}
                >
                  Próxima →
                </button>
              </div>
            )}
          </>
        ) : (
          <div style={{ padding: '4rem', textAlign: 'center', color: '#6b7280' }}>
            <svg style={{ 
              width: '64px', 
              height: '64px', 
              margin: '0 auto 1.5rem', 
              color: '#d1d5db' 
            }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem', color: '#374151' }}>
              Nenhuma oportunidade encontrada
            </h3>
            <p style={{ margin: 0, lineHeight: '1.6' }}>
              Ajuste os filtros ou clique em "Sincronizar Oportunidades" para importar dados da API M8.
            </p>
          </div>
        )}
      </div>

      {/* Modal de Sincronização */}
      {showSyncModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '16px',
            boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
            width: '90%',
            maxWidth: '500px',
            border: '1px solid #f1f5f9'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
              <div style={{
                padding: '0.5rem',
                backgroundColor: '#dcfce7',
                borderRadius: '8px'
              }}>
                <svg width="24" height="24" fill="none" stroke="currentColor" style={{ color: '#16a34a' }} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </div>
              <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem', fontWeight: '700' }}>
                Configurar Sincronização de Oportunidades
              </h3>
            </div>
            
            <div style={{ 
              backgroundColor: '#f0f9ff', 
              border: '1px solid #7dd3fc', 
              borderRadius: '10px', 
              padding: '1rem', 
              marginBottom: '1.5rem' 
            }}>
              <p style={{ margin: 0, fontSize: '0.875rem', color: '#0369a1', lineHeight: '1.5' }}>
                <strong>Paginação Inteligente:</strong> O sistema irá buscar TODAS as oportunidades do período especificado, 
                processando automaticamente todas as páginas da API M8 sem limitações.
              </p>
            </div>
            
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontSize: '0.875rem', 
                fontWeight: '600',
                color: '#374151'
              }}>
                Data Inicial: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="date"
                value={syncDates.startDate}
                onChange={(e) => setSyncDates({...syncDates, startDate: e.target.value})}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `2px solid ${!syncDates.startDate ? '#ef4444' : '#e5e7eb'}`,
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#16a34a'}
                onBlur={(e) => e.target.style.borderColor = !syncDates.startDate ? '#ef4444' : '#e5e7eb'}
              />
              {!syncDates.startDate && (
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#dc2626' }}>
                  Campo obrigatório
                </p>
              )}
            </div>
            
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '0.5rem', 
                fontSize: '0.875rem', 
                fontWeight: '600',
                color: '#374151'
              }}>
                Data Final: <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="date"
                value={syncDates.endDate}
                onChange={(e) => setSyncDates({...syncDates, endDate: e.target.value})}
                required
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `2px solid ${!syncDates.endDate ? '#ef4444' : '#e5e7eb'}`,
                  borderRadius: '8px',
                  fontSize: '0.875rem',
                  transition: 'border-color 0.2s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = '#16a34a'}
                onBlur={(e) => e.target.style.borderColor = !syncDates.endDate ? '#ef4444' : '#e5e7eb'}
              />
              {!syncDates.endDate && (
                <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#dc2626' }}>
                  Campo obrigatório
                </p>
              )}
            </div>
            
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowSyncModal(false)}
                style={{
                  padding: '0.75rem 1.5rem',
                  border: '2px solid #e5e7eb',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  color: '#374151',
                  transition: 'all 0.2s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.borderColor = '#d1d5db';
                  e.target.style.backgroundColor = '#f9fafb';
                }}
                onMouseOut={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.backgroundColor = 'white';
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleSync}
                disabled={!syncDates.startDate || !syncDates.endDate}
                style={{
                  padding: '0.75rem 1.5rem',
                  backgroundColor: (!syncDates.startDate || !syncDates.endDate) ? '#9ca3af' : '#16a34a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (!syncDates.startDate || !syncDates.endDate) ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: '600',
                  transition: 'background-color 0.2s ease',
                  boxShadow: (!syncDates.startDate || !syncDates.endDate) ? 'none' : '0 2px 4px rgba(22, 163, 74, 0.3)'
                }}
                onMouseOver={(e) => {
                  if (syncDates.startDate && syncDates.endDate) {
                    e.target.style.backgroundColor = '#15803d';
                  }
                }}
                onMouseOut={(e) => {
                  if (syncDates.startDate && syncDates.endDate) {
                    e.target.style.backgroundColor = '#16a34a';
                  }
                }}
              >
                Sincronizar Oportunidades
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CRMOpportunities;